// Third Party
import * as React from 'react';
import {
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  Transition,
} from '@headlessui/react';
import classNames from 'classnames';
import { Session } from 'next-auth';

// Utils
import { routes } from '@routes';
import { getReferralCode, removeReferralCode } from '@utils/referral-code';

// UI
import { Avatar } from '@components/avatar';
import { Icon, IconNames } from '@components/icon';
import { useLoginAndSignupModals } from '@contexts/use-login-and-signup-modals';
import { Link } from '@components/link';
import { signOut } from 'next-auth/react';
import { useUserAddReferralCodeMutation } from '@gql/generated';

type Props = {
  session: Session | null;
  loadingSession: boolean;
};

export const DesktopNav: React.FC<Props> = ({ session }) => {
  const { activateLoginModal } = useLoginAndSignupModals();
  const [addReferralCode] = useUserAddReferralCodeMutation();

  React.useEffect(() => {
    const referralCode = getReferralCode(undefined);
    if (referralCode) {
      addReferralCode({
        variables: {
          referralCode: referralCode,
        },
      }).then((result) => {
        if (!('errors' in (result.data?.userAddReferralCode || {}))) {
          removeReferralCode(undefined);
        }
      });
    }
  }, [addReferralCode]);

  const userNavigationSections: {
    name: string;
    href: VoidFunction | string;
    newWindow?: boolean;
  }[][] = [
    [
      {
        name: 'Favorites',
        href: session?.user ? routes.userAccountFavorites : activateLoginModal,
      },
      ...(session?.user
        ? [
            { name: 'Trips', href: routes.trips },
            { name: 'Manage Your Properties', href: routes.accountListings },
            { name: 'Account', href: routes.userAccountSettings },
          ]
        : [{ name: 'Traveler Signup', href: routes.createAccount }]),
    ],
    [
      { name: 'Gift Cards', href: routes.giftCardPurchase, newWindow: true },
      { name: 'Help', href: routes.help, newWindow: true },
      ...(session?.user
        ? [
            {
              name: 'Log out',
              href: () => signOut({ callbackUrl: '/', redirect: true }),
            },
          ]
        : [{ name: 'Log In', href: activateLoginModal }]),
    ],
  ];

  function checkNavType(
    name: string,
    active: boolean,
    href?: VoidFunction | string,
    newWindow?: boolean
  ) {
    if (typeof href === 'string') {
      return (
        <div key={name}>
          {!newWindow && (
            <a
              href={href}
              className={classNames(
                active ? 'bg-gray-xlight' : '',
                'block py-3 px-6 text-sm text-primary-main'
              )}
            >
              {name}
            </a>
          )}
          {newWindow && (
            <a
              href={href}
              className={classNames(
                active ? 'bg-gray-xlight' : '',
                'block py-3 px-6 text-sm text-primary-main'
              )}
              target="_blank"
              rel="noopener noreferrer"
            >
              {name}
            </a>
          )}
        </div>
      );
    } else if (typeof href === 'function') {
      return (
        <button
          style={{ outline: 'none' }}
          className={classNames(
            active ? 'bg-gray-xlight' : '',
            'block py-3 px-6 text-sm text-primary-main w-full text-start'
          )}
          onClick={async () => href()}
        >
          {name}
        </button>
      );
    } else {
      return (
        <div key={name}>
          <a
            className={classNames(
              active ? 'bg-gray-xlight' : '',
              'block py-3 px-6 text-sm text-primary-main'
            )}
          >
            {name}
          </a>
        </div>
      );
    }
  }

  return (
    <div>
      {/* <div
        className={cx(
          'bg-primary-main w-full h-14 flex flex-row justify-center text-white items-center',
          {
            'hidden h-0':
              router.pathname.match(/search/g) ||
              router.pathname.match(/\/s\//g) ||
              router.pathname.match(/gift-cards/g) ||
              router.pathname.match(/gc/g) ||
              router.pathname.match(/shop/g),
          }
        )}
      >
        <Link to={routes.giftCardPurchase} style={{ textDecoration: 'none' }}>
          <Typography
            component="p"
            variant="small"
            variantM="subtitle1"
            className="text-center text-white hover:underline"
          >
            GC Promo copy
          </Typography>
        </Link>
      </div> */}
      <div className="py-6">
        <div className="relative flex items-center justify-between">
          <Link to={routes.home}>
            <img
              src="/assets/svg/logos/savvy_wordmark_slate.svg"
              className="block h-10 w-auto"
              alt="logo"
            />
          </Link>

          <div className="hidden sm:flex">{/* Center column */}</div>

          <div className="flex items-center justify-end">
            {/* Show List Properties link outside menu on desktop */}
            {session?.user?.isHost ? (
              <Link
                to={routes.accountListings}
                className="ml-4 hidden text-sm md:inline-block"
              >
                Manage your Properties
              </Link>
            ) : (
              <Link
                to={routes.list}
                className="ml-4 hidden text-sm md:inline-block"
              >
                List your Properties
              </Link>
            )}

            <Menu as="div" className="relative z-50 ml-5 flex-shrink-0">
              <div>
                <MenuButton className="flex items-center rounded-full bg-white hover:shadow-lg">
                  <span className="sr-only">Open user menu</span>
                  <div className="flex items-center rounded-full border-[1.5px] border-primary-main py-1 pl-4 pr-1">
                    <Icon name={IconNames.BARS} className="mr-4" />
                    <div className="h-8 w-8">
                      <Avatar
                        firstName={session?.user?.firstName}
                        lastName={session?.user?.lastName}
                        avatarUrl={session?.user?.avatarUrl}
                        shape="circle"
                      />
                    </div>
                  </div>
                </MenuButton>
              </div>

              <Transition
                as="div"
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <MenuItems className="absolute right-0 mt-2 w-64 origin-top-right divide-y divide-primary-main rounded-md border-[1.5px] border-primary-main bg-white shadow-lg focus:outline-none">
                  {userNavigationSections.map((section, index) => (
                    <div key={index} className="py-2">
                      {section.map((item) => (
                        <MenuItem key={item.name}>
                          {({ active }) =>
                            checkNavType(
                              item.name,
                              active,
                              item.href,
                              item.newWindow
                            )
                          }
                        </MenuItem>
                      ))}
                    </div>
                  ))}
                </MenuItems>
              </Transition>
            </Menu>
          </div>
        </div>
      </div>
    </div>
  );
};
